import { useState, useRef, useCallback } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiryTime: number;
}

interface CacheOptions {
  // مدة انتهاء الصلاحية بالميللي ثانية
  ttl?: number;
  // الحد الأقصى لحجم الـ cache
  maxSize?: number;
}

export const useOptimizedCache = <T>(options: CacheOptions = {}) => {
  const {
    ttl = 5 * 60 * 1000, // 5 دقائق افتراضي
    maxSize = 100
  } = options;

  const cacheRef = useRef<Map<string, CacheEntry<T>>>(new Map());
  const [, forceUpdate] = useState({});

  const forceRefresh = useCallback(() => {
    forceUpdate({});
  }, []);

  const get = useCallback((key: string): T | null => {
    const cache = cacheRef.current;
    const entry = cache.get(key);

    if (!entry) {
      return null;
    }

    // فحص انتهاء الصلاحية
    if (Date.now() > entry.expiryTime) {
      cache.delete(key);
      return null;
    }

    return entry.data;
  }, []);

  const set = useCallback((key: string, data: T): void => {
    console.log('Setting cache for key:', key);
    console.log('Cache data:', data);
    const cache = cacheRef.current;

    // إزالة العناصر المنتهية الصلاحية والقديمة إذا تجاوز الحد الأقصى
    if (cache.size >= maxSize) {
      const now = Date.now();
      const entries = Array.from(cache.entries());
      
      // إزالة العناصر المنتهية الصلاحية أولاً
      const expiredKeys = entries
        .filter(([, entry]) => now > entry.expiryTime)
        .map(([key]) => key);
      
      expiredKeys.forEach(k => cache.delete(k));
      
      // إذا ما زال الحجم كبيراً، إزالة الأقدم
      if (cache.size >= maxSize) {
        const sortedEntries = entries
          .filter(([key]) => !expiredKeys.includes(key))
          .sort(([, a], [, b]) => a.timestamp - b.timestamp);
        
        const toRemove = Math.max(1, Math.floor(maxSize * 0.1)); // إزالة 10% من الأقدم
        for (let i = 0; i < toRemove && i < sortedEntries.length; i++) {
          cache.delete(sortedEntries[i][0]);
        }
      }
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiryTime: Date.now() + ttl
    };

    cache.set(key, entry);
  }, [ttl, maxSize]);

  const has = useCallback((key: string): boolean => {
    return get(key) !== null;
  }, [get]);

  const clear = useCallback((pattern?: string): void => {
    console.log('[useOptimizedCache] Clearing cache with pattern:', pattern);
    const cache = cacheRef.current;
    
    if (!pattern || pattern === undefined) {
      console.log('[useOptimizedCache] Clearing entire cache, size before:', cache.size);
      cache.clear();
      console.log('[useOptimizedCache] Cache cleared, size after:', cache.size);
      return;
    }

    console.log('[useOptimizedCache] Pattern provided:', pattern);
    // إزالة المفاتيح التي تطابق النمط
    const keysToDelete = Array.from(cache.keys()).filter(key => 
      key.includes(pattern)
    );
    
    console.log('[useOptimizedCache] Found', keysToDelete.length, 'keys to delete:', keysToDelete);
    keysToDelete.forEach(key => cache.delete(key));
    console.log('[useOptimizedCache] Cache size after deletion:', cache.size);
  }, []);

  const invalidate = useCallback((key: string): void => {
    cacheRef.current.delete(key);
  }, []);

  const size = useCallback((): number => {
    return cacheRef.current.size;
  }, []);

  return {
    get,
    set,
    has,
    clear,
    invalidate,
    size,
    forceRefresh
  };
};

